import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { notifyRoleGranted } from "../lib/notify";
import { BulkAddUsers } from "./BulkAddUsers";
import { Edit2 } from "lucide-react";
import { friendlyError } from "../lib/helpers";
import { PageHead, ConfirmDialog } from "../components/common";

// ─────────────────────────────────────────
//  SUPERADMIN SCREEN
// ─────────────────────────────────────────
function SuperAdminScreen({ onBack, lang, G, css, isSuperAdmin }) {
  const [activeTab, setActiveTab] = useState("users");
  const [orgs, setOrgs] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [courses, setCourses] = useState([]);
  const [instances, setInstances] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [expandedProgram, setExpandedProgram] = useState(null);
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [showForm, setShowForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [searchEmail, setSearchEmail] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterOrg, setFilterOrg] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [addRoleData, setAddRoleData] = useState({});
  const [editUserFields, setEditUserFields] = useState({ full_name: "", email: "" });
  const [editUserSaving, setEditUserSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // { table, id, field, value } or { table, id, fields: { name, year, term } }
  const [editItemSaving, setEditItemSaving] = useState(false);

  async function saveItemEdit() {
    if (!editingItem) return;
    setEditItemSaving(true);
    const { table, id, fields } = editingItem;
    const updates = {};
    if (fields) {
      Object.entries(fields).forEach(([k, v]) => { updates[k] = v === "" ? null : v; });
    }
    const { error } = await supabase.from(table).update(updates).eq("id", id);
    setEditItemSaving(false);
    if (error) { flash(friendlyError(error, lang), "err"); return; }
    setEditingItem(null);
    loadAll();
  }

  const flash = (text, type = "ok") => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), type === "err" ? 8000 : 3000);
  };

  // Bekräftelse i appens design i stället för native window.confirm()
  const [confirmState, setConfirmState] = useState(null); // { text, onConfirm }
  const askConfirm = (text, onConfirm) => setConfirmState({ text, onConfirm });

  // Inbjudna som inte loggat in än (pending_roles); syns inte bland användarna
  // eftersom kontot skapas först vid första inloggningen
  const [pendingInvites, setPendingInvites] = useState([]);
  const [cancelingInvite, setCancelingInvite] = useState(null);
  const loadPending = async () => {
    const { data, error } = await supabase.from("pending_roles")
      .select("id, email, lu_id, full_name, role, program_id, course_id, created_at")
      .is("claimed_at", null)
      .order("created_at", { ascending: false });
    setPendingInvites(error ? [] : (data || []));
  };
  const cancelInvite = (inv) => askConfirm(
    `Ta bort inbjudan för ${inv.full_name || inv.email || inv.lu_id}? Hen får då ingen behörighet vid inloggning.`,
    async () => {
      setCancelingInvite(inv.id);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch("/api/cancel-invite", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token}` },
          body: JSON.stringify({ id: inv.id }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) flash(json.error || `Kunde inte ta bort inbjudan (${res.status}).`, "err");
        else { flash("Inbjudan borttagen."); setPendingInvites(list => list.filter(x => x.id !== inv.id)); }
      } catch (e) { flash(friendlyError(e, lang), "err"); }
      setCancelingInvite(null);
    });

  const loadAll = async () => {
    setLoading(true);
    loadPending();
    const [o, p, c, i, r, u] = await Promise.all([
      supabase.from("organisations").select("*").order("name"),
      supabase.from("programs").select("*").order("name"),
      supabase.from("courses").select("*").order("name"),
      supabase.from("course_instances").select("*").order("year", { ascending: false }),
      supabase.from("user_roles").select("*").order("created_at", { ascending: false }),
      supabase.rpc("list_users_with_roles"),
    ]);
    if (o.data) setOrgs(o.data);
    if (p.data) setPrograms(p.data);
    if (c.data) setCourses(c.data);
    if (i.data) setInstances(i.data);
    if (r.data) setUserRoles(r.data);
    if (u.data) setAllUsers(u.data);
    else if (u.error) flash("Kunde inte ladda användarlistan. Kör list_users_with_roles.sql i Supabase SQL Editor.", "err");
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const inviteUser = async () => {
    if (!formData.invite_email?.trim()) return;
    if (!formData.role) { flash("Välj en roll.", "err"); return; }
    setInviting(true); setInviteResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${supabase.supabaseUrl}/functions/v1/invite-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session.access_token}`, "apikey": supabase.supabaseKey },
        body: JSON.stringify({ email: formData.invite_email.trim(), full_name: formData.invite_full_name?.trim() || null, role: formData.role, ...scopeFor(formData.role, formData) }),
      });
      const json = await res.json();
      if (!res.ok || json.error) { setInviteResult({ ok: false, text: `Fel (${res.status}): ${json.error || json.message || "Okänt fel"}` }); }
      else {
        // Fanns kontot redan läggs bara rollen till; då mejlar vi om den nya
        // behörigheten i stället för att skicka en inbjudan
        const email = formData.invite_email.trim();
        if (json.existed) {
          const sent = await notifyRoleGranted({ user_id: json.user_id, role: formData.role, ...scopeFor(formData.role, formData) });
          flash(sent
            ? `${email} fanns redan – rollen är tillagd och ett mejl om behörigheten skickades.`
            : `${email} fanns redan – rollen är tillagd. Inget mejl skickades.`);
        } else {
          flash(`Inbjudan skickad till ${email}`);
        }
        setShowForm(null); setFormData({}); setInviteResult(null);
        setTimeout(() => loadAll(), 1000);
      }
    } catch (e) { setInviteResult({ ok: false, text: friendlyError(e, lang) }); }
    setInviting(false);
  };

  const createOrg = async () => { if (!formData.name?.trim()) return; setSaving(true); const { error } = await supabase.from("organisations").insert({ name: formData.name.trim() }); setSaving(false); if (error) { flash(friendlyError(error, lang), "err"); return; } flash("Organisation skapad!"); setShowForm(null); setFormData({}); loadAll(); };
  const createProgram = async () => { if (!formData.name?.trim() || !formData.org_id) return; setSaving(true); const { error } = await supabase.from("programs").insert({ name: formData.name.trim(), org_id: formData.org_id }); setSaving(false); if (error) { flash(friendlyError(error, lang), "err"); return; } flash("Program skapat!"); setShowForm(null); setFormData({}); loadAll(); };
  const createCourse = async () => { if (!formData.name?.trim() || !formData.program_id) return; setSaving(true); const { error } = await supabase.from("courses").insert({ name: formData.name.trim(), program_id: formData.program_id }); setSaving(false); if (error) { flash(friendlyError(error, lang), "err"); return; } flash("Kurs skapad!"); setShowForm(null); setFormData({}); loadAll(); };
  const createInstance = async () => { if (!formData.name?.trim() || !formData.course_id) return; setSaving(true); const { error } = await supabase.from("course_instances").insert({ name: formData.name.trim(), course_id: formData.course_id, term: formData.term || null, year: formData.year ? parseInt(formData.year) : null }); setSaving(false); if (error) { flash(friendlyError(error, lang), "err"); return; } flash("Kurstillfälle skapat!"); setShowForm(null); setFormData({}); loadAll(); };

  const assignRole = async () => {
    if (!formData.user_id || !formData.role) return;
    setSaving(true);
    if (!scopeComplete(formData.role, formData)) { flash("Välj vad rollen ska gälla.", "err"); setSaving(false); return; }
    const { error } = await supabase.from("user_roles").insert({ user_id: formData.user_id, role: formData.role, ...scopeFor(formData.role, formData) });
    setSaving(false);
    if (error) { flash(friendlyError(error, lang), "err"); return; }
    const sent = await notifyRoleGranted({ user_id: formData.user_id, role: formData.role, ...scopeFor(formData.role, formData) });
    flash(sent ? "Roll tilldelad – ett mejl om behörigheten skickades." : "Roll tilldelad!"); setShowForm(null); setFormData({}); setSearchResult(null); setSearchEmail(""); loadAll();
  };

  const deleteItem = (table, id) => {
    askConfirm("Är du säker? Detta kan inte ångras.", async () => {
      await supabase.from(table).delete().eq("id", id);
      flash("Borttaget."); loadAll();
    });
  };

  const assignRoleToUser = async () => {
    if (!editingUser?.user_id || !addRoleData.role) return;
    if (!scopeComplete(addRoleData.role, addRoleData)) {
      flash("Välj vad rollen ska gälla — programadmin kräver program, kursansvarig kräver kurs.", "err"); return;
    }
    setSaving(true);
    const { error } = await supabase.from("user_roles").insert({ user_id: editingUser.user_id, role: addRoleData.role, ...scopeFor(addRoleData.role, addRoleData) });
    setSaving(false);
    if (error) { flash(friendlyError(error, lang), "err"); return; }
    const sent = await notifyRoleGranted({ user_id: editingUser.user_id, role: addRoleData.role, ...scopeFor(addRoleData.role, addRoleData) });
    flash(sent ? "Roll tillagd – ett mejl om behörigheten skickades." : "Roll tillagd!"); setAddRoleData({});
    const [r, u] = await Promise.all([supabase.from("user_roles").select("*").order("created_at", { ascending: false }), supabase.rpc("list_users_with_roles")]);
    if (r.data) setUserRoles(r.data);
    if (u.data) { setAllUsers(u.data); const refreshed = u.data.find(x => x.user_id === editingUser.user_id); if (refreshed) setEditingUser(refreshed); }
  };

  const deleteRoleFromUser = (roleId) => {
    askConfirm("Ta bort rollen?", async () => {
      await supabase.from("user_roles").delete().eq("id", roleId);
      flash("Roll borttagen.");
      const [r, u] = await Promise.all([supabase.from("user_roles").select("*").order("created_at", { ascending: false }), supabase.rpc("list_users_with_roles")]);
      if (r.data) setUserRoles(r.data);
      if (u.data) { setAllUsers(u.data); const refreshed = u.data.find(x => x.user_id === editingUser.user_id); if (refreshed) setEditingUser(refreshed); }
    });
  };

  const updateUserProfile = async () => {
    if (!editingUser) return;
    setEditUserSaving(true);
    try {
      const { data: { session: sess } } = await supabase.auth.getSession();
      const res = await fetch(`${supabase.supabaseUrl}/functions/v1/manage-user`, { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${sess?.access_token}`, "apikey": supabase.supabaseKey }, body: JSON.stringify({ action: "update", user_id: editingUser.user_id, full_name: editUserFields.full_name, email: editUserFields.email }) });
      const json = await res.json();
      if (!res.ok || json.error) { flash(`Fel: ${json.error || json.message}`, "err"); }
      else { flash("Användare uppdaterad!"); const { data } = await supabase.rpc("list_users_with_roles"); if (data) { setAllUsers(data); const r = data.find(x => x.user_id === editingUser.user_id); if (r) setEditingUser(r); } }
    } catch (e) { flash(friendlyError(e, lang), "err"); }
    setEditUserSaving(false);
  };

  const deleteUserAccount = async () => {
    if (!editingUser) return;
    setEditUserSaving(true);
    try {
      const { data: { session: sess } } = await supabase.auth.getSession();
      const res = await fetch(`${supabase.supabaseUrl}/functions/v1/manage-user`, { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${sess?.access_token}`, "apikey": supabase.supabaseKey }, body: JSON.stringify({ action: "delete", user_id: editingUser.user_id }) });
      const json = await res.json();
      if (!res.ok || json.error) { flash(`Fel: ${json.error || json.message}`, "err"); }
      else { flash("Användare borttagen."); setShowForm(null); setEditingUser(null); setDeleteConfirm(false); loadAll(); }
    } catch (e) { flash(friendlyError(e, lang), "err"); }
    setEditUserSaving(false);
  };

  // ── Design tokens ──────────────────────────────────────────────
  // Role: single accent color (gold), neutral text for context
  const roleLabels = { superadmin: "Superadmin", program_admin: "Programadmin", course_responsible: "Kursansvarig" };
  // Bara superadmin får utse superadminer och programadminer utanför sina egna
  // program; RLS stoppar resten, men menyn ska inte erbjuda det heller.
  const assignableRoles = isSuperAdmin
    ? roleLabels
    : { program_admin: "Programadmin", course_responsible: "Kursansvarig" };

  // Rollen avgör VILKEN scope-kolumn som skrivs. Org och program är bara vägen
  // fram i formuläret; skrevs de med följde behörigheten med dem, och en
  // kursansvarig blev medlärare i hela organisationen. CHECK-villkoret i
  // databasen vägrar numera sådana rader — det här håller UI:t i takt.
  function scopeFor(role, d) {
    if (role === "program_admin") return { program_id: d.program_id || null };
    if (role === "course_responsible") return { course_id: d.course_id || null };
    return {};
  }
  function scopeComplete(role, d) {
    if (role === "program_admin") return !!d.program_id;
    if (role === "course_responsible") return !!d.course_id;
    return role === "superadmin";
  }
  const programLabel = pr => {
    const org = orgs.find(o => o.id === pr.org_id);
    return org ? `${org.name} · ${pr.name}` : pr.name;
  };
  // Gemensam scope-väljare för formulärets tre ställen
  const RoleScope = ({ d, set }) => (<>
    {["program_admin", "course_responsible"].includes(d.role) && (<>
      <FL>Program</FL>
      <select style={css.input} value={d.program_id || ""} onChange={e => set({ ...d, program_id: e.target.value, course_id: "" })}>
        <option value="">Välj program…</option>
        {programs.map(pr => <option key={pr.id} value={pr.id}>{programLabel(pr)}</option>)}
      </select>
    </>)}
    {d.role === "course_responsible" && d.program_id && (<>
      <FL>Kurs</FL>
      <select style={css.input} value={d.course_id || ""} onChange={e => set({ ...d, course_id: e.target.value })}>
        <option value="">Välj kurs…</option>
        {courses.filter(c => c.program_id === d.program_id).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
    </>)}
  </>);

  // Subtle, single-hue badges: just a dim background + text, no bright borders
  const RoleBadge = ({ role, context }) => {
    const label = roleLabels[role] || role;
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: `${G.surface2}`, border: `1px solid ${G.border}`, borderRadius: 4, padding: "3px 8px", fontSize: 12, fontFamily: "'DM Mono', monospace", color: G.text, letterSpacing: "0.02em", whiteSpace: "nowrap" }}>
        <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: role === "superadmin" ? G.gold : role === "org_admin" ? "#94a3b8" : role === "program_admin" ? "#94a3b8" : G.muted, flexShrink: 0 }} />
        {label}{context ? <span style={{ color: G.muted, fontFamily: "'DM Sans', sans-serif", fontSize: 12 }}> · {context}</span> : ""}
      </span>
    );
  };

  // Icon button: just text/icon, no colored borders
  const IconBtn = ({ onClick, title, danger, children }) => (
    <button onClick={onClick} title={title} style={{ background: "none", border: "none", cursor: "pointer", color: danger ? G.red : G.muted, fontSize: 14, padding: "4px 8px", borderRadius: 4, display: "inline-flex", alignItems: "center", opacity: 0.85, transition: "opacity 0.15s" }}
      onMouseEnter={e => e.currentTarget.style.opacity = "1"}
      onMouseLeave={e => e.currentTarget.style.opacity = "0.7"}
    >{children}</button>
  );

  // Compact action button for tree (no colored borders)
  const ActionBtn = ({ onClick, children }) => (
    <button onClick={onClick} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 4, color: G.muted, fontSize: 12, fontFamily: "'DM Sans', sans-serif", padding: "3px 10px", cursor: "pointer", whiteSpace: "nowrap" }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = G.text; e.currentTarget.style.color = G.text; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = G.border; e.currentTarget.style.color = G.muted; }}
    >{children}</button>
  );

  // Tab nav
  const tabStyle = (id) => ({
    padding: "10px 20px", background: "none", border: "none",
    borderBottom: activeTab === id ? `2px solid ${G.gold}` : "2px solid transparent",
    color: activeTab === id ? G.text : G.muted,
    fontFamily: "'DM Sans', sans-serif", fontWeight: activeTab === id ? 600 : 400,
    fontSize: 14, cursor: "pointer", whiteSpace: "nowrap", letterSpacing: "0.01em",
  });

  // Modal form label
  const FL = ({ children }) => <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: G.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5, marginTop: 14 }}>{children}</label>;

  // Avatar initials
  const Avatar = ({ name, email }) => {
    const ch = ((name || email || "?")[0] || "?").toUpperCase();
    return <div style={{ width: 32, height: 32, borderRadius: "50%", background: G.surface2, border: `1px solid ${G.border}`, color: G.text, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{ch}</div>;
  };

  // SVG Chevron
  const Chevron = ({ open }) => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transition: "transform 0.2s", transform: open ? "rotate(90deg)" : "rotate(0deg)", flexShrink: 0 }}>
      <path d="M4 2l4 4-4 4" stroke={G.muted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  // SVG Trash
  const TrashIcon = () => (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path d="M2 4h12M5 4V2.5A.5.5 0 015.5 2h5a.5.5 0 01.5.5V4M6 7v5M10 7v5M3 4l1 9.5A.5.5 0 004.5 14h7a.5.5 0 00.497-.45L13 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  // SVG Plus
  const PlusIcon = () => (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
      <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );

  return (
    <div style={css.page}>

      {/* Flash message */}
      {msg && (
        <div style={{ position: "fixed", top: 16, right: 16, zIndex: 9999, background: msg.type === "err" ? "#1e1010" : "#0f1a0f", border: `1px solid ${msg.type === "err" ? G.red : G.correct}`, color: msg.type === "err" ? G.red : G.correct, borderRadius: 8, padding: "10px 16px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500, boxShadow: "0 4px 24px rgba(0,0,0,0.4)", maxWidth: 380 }}>
          {msg.text}
        </div>
      )}

      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "36px 24px 0" }}>
        <PageHead
          G={G}
          title={lang === "sv" ? "Administration" : "Administration"}
          sub={lang === "sv" ? "Användare, organisationer och rollöversikt." : "Users, organisations and role overview."}
        />
        {/* Tab navigation */}
        <div style={{ display: "flex", borderBottom: `1px solid ${G.border}`, marginBottom: 32 }}>
          <button style={tabStyle("users")} onClick={() => setActiveTab("users")}>Användare</button>
          {isSuperAdmin && <button style={tabStyle("orgs")} onClick={() => setActiveTab("orgs")}>Organisationsstruktur</button>}
          <button style={tabStyle("roles")} onClick={() => setActiveTab("roles")}>Rollöversikt</button>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", color: G.muted, padding: 60, fontSize: 14 }}>Laddar…</div>
        ) : activeTab === "users" ? (

          /* ─── USERS TAB ─────────────────────────────────────── */
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, gap: 16, flexWrap: "wrap" }}>
              <div>
                <h2 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 18, color: G.text, margin: 0 }}>Användare</h2>
                <p style={{ color: G.muted, fontSize: 13, margin: "3px 0 0" }}>{allUsers.length} registrerade{pendingInvites.length > 0 ? ` · ${pendingInvites.length} väntar på första inloggning` : ""}</p>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={loadAll} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 6, color: G.muted, fontSize: 13, padding: "7px 12px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>Uppdatera</button>
                <button onClick={() => setShowForm("bulk")} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 6, color: G.text, fontSize: 14, padding: "7px 14px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>Lägg till flera</button>
                <button onClick={() => { setShowForm("invite"); setFormData({}); setInviteResult(null); }} style={{ background: G.gold, border: "none", borderRadius: 6, color: G.bg, fontSize: 14, fontWeight: 600, padding: "7px 16px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>+ Lägg till användare</button>
              </div>
            </div>

            {/* Filter bar */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
              <div style={{ position: "relative", flex: "1 1 220px" }}>
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}><circle cx="6" cy="6" r="4.5" stroke={G.muted} strokeWidth="1.4"/><path d="M10 10l2.5 2.5" stroke={G.muted} strokeWidth="1.4" strokeLinecap="round"/></svg>
                <input style={{ ...css.input, paddingLeft: 30, marginBottom: 0, fontSize: 14 }} placeholder="Sök namn eller e-post…" value={userSearch} onChange={e => setUserSearch(e.target.value)} />
              </div>
              <select style={{ ...css.input, flex: "0 1 150px", marginBottom: 0, fontSize: 14 }} value={filterRole} onChange={e => setFilterRole(e.target.value)}>
                <option value="">Alla roller</option>
                {Object.entries(roleLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <select style={{ ...css.input, flex: "0 1 180px", marginBottom: 0, fontSize: 14 }} value={filterOrg} onChange={e => setFilterOrg(e.target.value)}>
                <option value="">Alla organisationer</option>
                {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>

            {/* User table */}
            <div style={{ border: `1px solid ${G.border}`, borderRadius: 12, overflow: "hidden" }}>
              {/* Table header */}
              <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr 3fr auto", gap: 0, background: G.surface2, borderBottom: `1px solid ${G.border}`, padding: "8px 16px" }}>
                {["Namn", "E-post", "Roll & tillhörighet", ""].map((h, i) => (
                  <span key={i} style={{ fontSize: 12, fontWeight: 600, color: G.muted, textTransform: "uppercase", letterSpacing: "0.07em" }}>{h}</span>
                ))}
              </div>

              {(() => {
                const q = userSearch.trim().toLowerCase();
                const filtered = allUsers.filter(u => {
                  const matchSearch = !q || u.email?.toLowerCase().includes(q) || u.full_name?.toLowerCase().includes(q);
                  const matchRole = !filterRole || u.roles.some(r => r.role === filterRole);
                  const matchOrg = !filterOrg || u.roles.some(r => r.org_id === filterOrg);
                  return matchSearch && matchRole && matchOrg;
                });
                if (filtered.length === 0) return (
                  <div style={{ textAlign: "center", color: G.muted, padding: "32px 16px", fontSize: 14 }}>Inga användare matchar filtret.</div>
                );
                return filtered.map((u, idx) => (
                  <div key={u.user_id}
                    style={{ display: "grid", gridTemplateColumns: "2fr 2fr 3fr auto", gap: 0, padding: "11px 16px", borderBottom: idx < filtered.length - 1 ? `1px solid ${G.border}` : "none", alignItems: "center", cursor: "pointer", transition: "background 0.1s" }}
                    onClick={() => { setEditingUser(u); setShowForm("editUser"); setAddRoleData({}); setDeleteConfirm(false); setEditUserFields({ full_name: u.full_name || "", email: u.email || "" }); }}
                    onMouseEnter={e => e.currentTarget.style.background = G.surface2}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                      <Avatar name={u.full_name} email={u.email} />
                      <span style={{ fontSize: 14, fontWeight: 500, color: G.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.full_name || <span style={{ color: G.muted, fontStyle: "italic" }}>Inget namn</span>}</span>
                    </div>
                    <span style={{ fontSize: 13, color: G.muted, fontFamily: "'DM Mono', monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 12 }}>{u.email}</span>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {u.roles.length === 0 && <span style={{ fontSize: 12, color: G.muted, fontStyle: "italic" }}>Ingen roll</span>}
                      {u.roles.slice(0, 2).map(r => {
                        const ctx = [orgs.find(o => o.id === r.org_id)?.name, programs.find(p => p.id === r.program_id)?.name].filter(Boolean).join(" › ");
                        return <RoleBadge key={r.id} role={r.role} context={ctx} />;
                      })}
                      {u.roles.length > 2 && <span style={{ fontSize: 12, color: G.muted, padding: "3px 6px" }}>+{u.roles.length - 2}</span>}
                    </div>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ color: G.muted, opacity: 0.6 }}><path d="M5 2l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                ));
              })()}
            </div>

            {/* Väntar på första inloggning */}
            {pendingInvites.length > 0 && (() => {
              const q = userSearch.trim().toLowerCase();
              const list = pendingInvites.filter(inv => {
                const matchSearch = !q || [inv.full_name, inv.email, inv.lu_id].some(v => v?.toLowerCase().includes(q));
                const matchRole = !filterRole || inv.role === filterRole;
                return matchSearch && matchRole;
              });
              const scopeLabel = inv => inv.role === "course_responsible"
                ? (courses.find(c => c.id === inv.course_id)?.name || "Okänd kurs")
                : inv.role === "program_admin" ? (programs.find(p => p.id === inv.program_id)?.name || "Okänt program") : "";
              return (
                <div style={{ marginTop: 28 }}>
                  <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 15, color: G.text, margin: 0 }}>Väntar på första inloggning</h3>
                  <p style={{ color: G.muted, fontSize: 13, margin: "3px 0 12px", lineHeight: 1.5 }}>
                    {pendingInvites.length} inbjudna som inte har loggat in än. De får sin behörighet när de loggar in med LU första gången, och flyttar då upp till listan ovan.
                  </p>
                  <div style={{ border: `1px solid ${G.border}`, borderRadius: 12, overflow: "hidden" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr 3fr 1fr auto", background: G.surface2, borderBottom: `1px solid ${G.border}`, padding: "8px 16px" }}>
                      {["Namn", "E-post / LU-id", "Roll", "Inbjuden", ""].map((h, i) => (
                        <span key={i} style={{ fontSize: 12, fontWeight: 600, color: G.muted, textTransform: "uppercase", letterSpacing: "0.07em" }}>{h}</span>
                      ))}
                    </div>
                    {list.length === 0 ? (
                      <div style={{ textAlign: "center", color: G.muted, padding: "20px 16px", fontSize: 14 }}>Inga inbjudna matchar filtret.</div>
                    ) : list.map((inv, idx) => (
                      <div key={inv.id} style={{ display: "grid", gridTemplateColumns: "2fr 2fr 3fr 1fr auto", alignItems: "center", padding: "10px 16px", borderBottom: idx < list.length - 1 ? `1px solid ${G.border}` : "none" }}>
                        <span style={{ fontSize: 14, color: G.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 12 }}>{inv.full_name || <span style={{ color: G.muted, fontStyle: "italic" }}>Inget namn</span>}</span>
                        <span style={{ fontSize: 13, color: G.muted, fontFamily: "'DM Mono', monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 12 }}>
                          {inv.email || "–"}{inv.lu_id ? <span style={{ display: "block", fontSize: 12 }}>LU-id {inv.lu_id}</span> : null}
                        </span>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          <RoleBadge role={inv.role} context={scopeLabel(inv)} />
                        </div>
                        <span style={{ fontSize: 12, color: G.muted, fontFamily: "'DM Mono', monospace" }}>
                          {new Date(inv.created_at).toLocaleDateString("sv-SE", { day: "numeric", month: "short" })}
                        </span>
                        <button onClick={() => cancelInvite(inv)} disabled={cancelingInvite === inv.id}
                          aria-label={`Ta bort inbjudan för ${inv.full_name || inv.email || inv.lu_id}`}
                          style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 6, color: G.muted, fontSize: 12, padding: "4px 10px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", opacity: cancelingInvite === inv.id ? 0.5 : 1 }}>
                          Ta bort
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>

        ) : activeTab === "orgs" ? (

          /* ─── ORGS TAB ──────────────────────────────────────── */
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
              <div>
                <h2 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 18, color: G.text, margin: 0 }}>Organisationsstruktur</h2>
                <p style={{ color: G.muted, fontSize: 13, margin: "3px 0 0" }}>{orgs.length} organisationer</p>
              </div>
              <button onClick={() => { setShowForm("org"); setFormData({}); }} style={{ background: G.gold, border: "none", borderRadius: 6, color: G.bg, fontSize: 14, fontWeight: 600, padding: "7px 16px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>+ Organisation</button>
            </div>

            {orgs.length === 0 && <div style={{ textAlign: "center", color: G.muted, padding: "48px 16px", fontSize: 14, border: `1px dashed ${G.border}`, borderRadius: 12 }}>Inga organisationer ännu.</div>}

            <div style={{ display: "grid", gap: 8 }}>
              {orgs.map(org => (
                <div key={org.id} style={{ border: `1px solid ${G.border}`, borderRadius: 12, overflow: "hidden" }}>
                  {/* Org row */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: G.surface, cursor: "pointer" }} onClick={() => setExpandedOrg(expandedOrg === org.id ? null : org.id)}>
                    <Chevron open={expandedOrg === org.id} />
                    {editingItem?.table === "organisations" && editingItem?.id === org.id ? (
                      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6 }} onClick={e => e.stopPropagation()}>
                        <input autoFocus value={editingItem.fields.name} onChange={e => setEditingItem(p => ({ ...p, fields: { ...p.fields, name: e.target.value } }))} style={{ fontWeight: 600, fontSize: 14, color: G.text, background: G.surface2, border: `1px solid ${G.border}`, borderRadius: 5, padding: "3px 8px", flex: 1, outline: "none", fontFamily: "'Plus Jakarta Sans', sans-serif" }} onKeyDown={e => { if (e.key === "Enter") saveItemEdit(); if (e.key === "Escape") setEditingItem(null); }} />
                        <button onClick={saveItemEdit} disabled={editItemSaving} style={{ background: G.accent, color: "#fff", border: "none", borderRadius: 5, fontSize: 11, fontWeight: 600, padding: "3px 10px", cursor: "pointer" }}>{editItemSaving ? "…" : "Spara"}</button>
                        <button onClick={() => setEditingItem(null)} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 5, color: G.muted, fontSize: 11, padding: "3px 8px", cursor: "pointer" }}>✕</button>
                      </div>
                    ) : (
                      <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: G.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{org.name}</span>
                    )}
                    <span style={{ fontSize: 12, color: G.muted }}>{programs.filter(p => p.org_id === org.id).length} program</span>
                    {!(editingItem?.table === "organisations" && editingItem?.id === org.id) && <IconBtn onClick={e => { e.stopPropagation(); setEditingItem({ table: "organisations", id: org.id, fields: { name: org.name } }); }} title="Redigera"><Edit2 size={13} /></IconBtn>}
                    <ActionBtn onClick={e => { e.stopPropagation(); setShowForm("program"); setFormData({ org_id: org.id }); }}><PlusIcon /> Program</ActionBtn>
                    <IconBtn danger onClick={e => { e.stopPropagation(); deleteItem("organisations", org.id); }} title="Ta bort"><TrashIcon /></IconBtn>
                  </div>

                  {expandedOrg === org.id && (
                    <div style={{ background: G.surface2, borderTop: `1px solid ${G.border}`, paddingLeft: 0 }}>
                      {programs.filter(p => p.org_id === org.id).length === 0 && <p style={{ color: G.muted, fontSize: 13, padding: "12px 20px", margin: 0 }}>Inga program.</p>}
                      {programs.filter(p => p.org_id === org.id).map(prog => (
                        <div key={prog.id} style={{ borderBottom: `1px solid ${G.border}` }}>
                          {/* Program row */}
                          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px 10px 32px", cursor: "pointer" }} onClick={() => setExpandedProgram(expandedProgram === prog.id ? null : prog.id)}>
                            <Chevron open={expandedProgram === prog.id} />
                            {editingItem?.table === "programs" && editingItem?.id === prog.id ? (
                              <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6 }} onClick={e => e.stopPropagation()}>
                                <input autoFocus value={editingItem.fields.name} onChange={e => setEditingItem(p => ({ ...p, fields: { ...p.fields, name: e.target.value } }))} style={{ fontSize: 14, color: G.text, background: G.surface, border: `1px solid ${G.border}`, borderRadius: 5, padding: "3px 8px", flex: 1, outline: "none", fontWeight: 500 }} onKeyDown={e => { if (e.key === "Enter") saveItemEdit(); if (e.key === "Escape") setEditingItem(null); }} />
                                <button onClick={saveItemEdit} disabled={editItemSaving} style={{ background: G.accent, color: "#fff", border: "none", borderRadius: 5, fontSize: 11, fontWeight: 600, padding: "3px 10px", cursor: "pointer" }}>{editItemSaving ? "…" : "Spara"}</button>
                                <button onClick={() => setEditingItem(null)} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 5, color: G.muted, fontSize: 11, padding: "3px 8px", cursor: "pointer" }}>✕</button>
                              </div>
                            ) : (
                              <span style={{ flex: 1, fontSize: 14, color: G.text, fontWeight: 500 }}>{prog.name}</span>
                            )}
                            <span style={{ fontSize: 12, color: G.muted }}>{courses.filter(c => c.program_id === prog.id).length} kurser</span>
                            {!(editingItem?.table === "programs" && editingItem?.id === prog.id) && <IconBtn onClick={e => { e.stopPropagation(); setEditingItem({ table: "programs", id: prog.id, fields: { name: prog.name } }); }} title="Redigera"><Edit2 size={13} /></IconBtn>}
                            <ActionBtn onClick={e => { e.stopPropagation(); setShowForm("course"); setFormData({ program_id: prog.id, org_id: org.id }); }}><PlusIcon /> Kurs</ActionBtn>
                            <IconBtn danger onClick={e => { e.stopPropagation(); deleteItem("programs", prog.id); }} title="Ta bort"><TrashIcon /></IconBtn>
                          </div>

                          {expandedProgram === prog.id && (
                            <div style={{ paddingLeft: 56 }}>
                              {courses.filter(c => c.program_id === prog.id).length === 0 && <p style={{ color: G.muted, fontSize: 13, padding: "10px 16px", margin: 0 }}>Inga kurser.</p>}
                              {courses.filter(c => c.program_id === prog.id).map(course => (
                                <div key={course.id}>
                                  {/* Course row */}
                                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", borderTop: `1px solid ${G.border}`, cursor: "pointer" }} onClick={() => setExpandedCourse(expandedCourse === course.id ? null : course.id)}>
                                    <Chevron open={expandedCourse === course.id} />
                                    {editingItem?.table === "courses" && editingItem?.id === course.id ? (
                                      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6 }} onClick={e => e.stopPropagation()}>
                                        <input autoFocus value={editingItem.fields.name} onChange={e => setEditingItem(p => ({ ...p, fields: { ...p.fields, name: e.target.value } }))} style={{ fontSize: 13, color: G.text, background: G.surface, border: `1px solid ${G.border}`, borderRadius: 5, padding: "3px 8px", flex: 1, outline: "none" }} onKeyDown={e => { if (e.key === "Enter") saveItemEdit(); if (e.key === "Escape") setEditingItem(null); }} />
                                        <button onClick={saveItemEdit} disabled={editItemSaving} style={{ background: G.accent, color: "#fff", border: "none", borderRadius: 5, fontSize: 11, fontWeight: 600, padding: "3px 10px", cursor: "pointer" }}>{editItemSaving ? "…" : "Spara"}</button>
                                        <button onClick={() => setEditingItem(null)} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 5, color: G.muted, fontSize: 11, padding: "3px 8px", cursor: "pointer" }}>✕</button>
                                      </div>
                                    ) : (
                                      <span style={{ flex: 1, fontSize: 13, color: G.text }}>{course.name}</span>
                                    )}
                                    <span style={{ fontSize: 12, color: G.muted }}>{instances.filter(i => i.course_id === course.id).length} tillfällen</span>
                                    {!(editingItem?.table === "courses" && editingItem?.id === course.id) && <IconBtn onClick={e => { e.stopPropagation(); setEditingItem({ table: "courses", id: course.id, fields: { name: course.name } }); }} title="Redigera"><Edit2 size={13} /></IconBtn>}
                                    <ActionBtn onClick={e => { e.stopPropagation(); setShowForm("instance"); setFormData({ course_id: course.id }); }}><PlusIcon /> Tillfälle</ActionBtn>
                                    <IconBtn danger onClick={e => { e.stopPropagation(); deleteItem("courses", course.id); }} title="Ta bort"><TrashIcon /></IconBtn>
                                  </div>

                                  {expandedCourse === course.id && (
                                    <div style={{ paddingLeft: 24, borderTop: `1px solid ${G.border}` }}>
                                      {instances.filter(i => i.course_id === course.id).length === 0 && <p style={{ color: G.muted, fontSize: 13, padding: "8px 16px", margin: 0 }}>Inga kurstillfällen.</p>}
                                      {instances.filter(i => i.course_id === course.id).map(inst => (
                                        <div key={inst.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 16px", borderBottom: `1px solid ${G.border}` }}>
                                          {editingItem?.table === "course_instances" && editingItem?.id === inst.id ? (
                                            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6 }}>
                                              <input autoFocus value={editingItem.fields.name || ""} onChange={e => setEditingItem(p => ({ ...p, fields: { ...p.fields, name: e.target.value } }))} placeholder="Namn" style={{ fontSize: 13, color: G.text, background: G.surface, border: `1px solid ${G.border}`, borderRadius: 5, padding: "3px 8px", flex: 1, outline: "none" }} onKeyDown={e => { if (e.key === "Enter") saveItemEdit(); if (e.key === "Escape") setEditingItem(null); }} />
                                              <input value={editingItem.fields.term || ""} onChange={e => setEditingItem(p => ({ ...p, fields: { ...p.fields, term: e.target.value } }))} placeholder="Termin" style={{ fontSize: 13, color: G.text, background: G.surface, border: `1px solid ${G.border}`, borderRadius: 5, padding: "3px 8px", width: 70, outline: "none" }} onKeyDown={e => { if (e.key === "Enter") saveItemEdit(); if (e.key === "Escape") setEditingItem(null); }} />
                                              <input type="number" value={editingItem.fields.year || ""} onChange={e => setEditingItem(p => ({ ...p, fields: { ...p.fields, year: e.target.value } }))} placeholder="År" style={{ fontSize: 13, color: G.text, background: G.surface, border: `1px solid ${G.border}`, borderRadius: 5, padding: "3px 8px", width: 60, outline: "none" }} onKeyDown={e => { if (e.key === "Enter") saveItemEdit(); if (e.key === "Escape") setEditingItem(null); }} />
                                              <button onClick={saveItemEdit} disabled={editItemSaving} style={{ background: G.accent, color: "#fff", border: "none", borderRadius: 5, fontSize: 11, fontWeight: 600, padding: "3px 10px", cursor: "pointer" }}>{editItemSaving ? "…" : "Spara"}</button>
                                              <button onClick={() => setEditingItem(null)} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 5, color: G.muted, fontSize: 11, padding: "3px 8px", cursor: "pointer" }}>✕</button>
                                            </div>
                                          ) : (
                                            <>
                                              <span style={{ fontSize: 13, color: G.text, flex: 1 }}>{inst.name}{inst.term ? " · " + inst.term : ""}{inst.year ? " " + inst.year : ""}</span>
                                              <IconBtn onClick={() => setEditingItem({ table: "course_instances", id: inst.id, fields: { name: inst.name || "", term: inst.term || "", year: inst.year || "" } })} title="Redigera"><Edit2 size={13} /></IconBtn>
                                            </>
                                          )}
                                          {!(editingItem?.table === "course_instances" && editingItem?.id === inst.id) && <IconBtn danger onClick={() => deleteItem("course_instances", inst.id)} title="Ta bort"><TrashIcon /></IconBtn>}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

        ) : activeTab === "roles" ? (

          /* ─── ROLES TAB ─────────────────────────────────────── */
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
              <div>
                <h2 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 18, color: G.text, margin: 0 }}>Rollöversikt</h2>
                <p style={{ color: G.muted, fontSize: 13, margin: "3px 0 0" }}>{userRoles.length} tilldelade roller</p>
              </div>
              <button onClick={() => { setShowForm("role"); setFormData({}); setSearchResult(null); setSearchEmail(""); }} style={{ background: G.gold, border: "none", borderRadius: 6, color: G.bg, fontSize: 14, fontWeight: 600, padding: "7px 16px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>+ Tilldela roll</button>
            </div>

            {userRoles.length === 0 && <div style={{ textAlign: "center", color: G.muted, padding: "48px 16px", fontSize: 14, border: `1px dashed ${G.border}`, borderRadius: 12 }}>Inga roller tilldelade ännu.</div>}

            <div style={{ border: `1px solid ${G.border}`, borderRadius: 12, overflow: "hidden" }}>
              {userRoles.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 2fr auto", padding: "8px 16px", background: G.surface2, borderBottom: `1px solid ${G.border}` }}>
                  {["Roll", "Tillhörighet", "Användare", ""].map((h, i) => <span key={i} style={{ fontSize: 12, fontWeight: 600, color: G.muted, textTransform: "uppercase", letterSpacing: "0.07em" }}>{h}</span>)}
                </div>
              )}
              {userRoles.map((r, idx) => {
                const orgName = orgs.find(o => o.id === r.org_id)?.name;
                const progName = programs.find(p => p.id === r.program_id)?.name;
                const courseName = courses.find(c => c.id === r.course_id)?.name;
                return (
                  <div key={r.id} style={{ display: "grid", gridTemplateColumns: "1fr 2fr 2fr auto", padding: "10px 16px", borderBottom: idx < userRoles.length - 1 ? `1px solid ${G.border}` : "none", alignItems: "center" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: G.text }}>{roleLabels[r.role] || r.role}</span>
                    <span style={{ fontSize: 13, color: G.muted }}>{[orgName, progName, courseName].filter(Boolean).join(" › ") || "—"}</span>
                    <span style={{ fontSize: 12, color: G.muted, fontFamily: "'DM Mono', monospace", overflow: "hidden", textOverflow: "ellipsis" }}>{r.user_id}</span>
                    <IconBtn danger onClick={() => deleteItem("user_roles", r.id)} title="Ta bort roll"><TrashIcon /></IconBtn>
                  </div>
                );
              })}
            </div>
          </div>

        ) : null}
      </div>

      {/* ─── MODAL ───────────────────────────────────────────── */}
      {showForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 10000 }}
          onClick={() => { if (showForm === "bulk") return; setShowForm(null); setEditingUser(null); setAddRoleData({}); }}>
          <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: "24px 28px", maxWidth: showForm === "bulk" ? 720 : 480, width: "100%", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}
            onClick={e => e.stopPropagation()}>

            {/* ── Edit User ── */}
            {showForm === "editUser" && editingUser && (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${G.border}` }}>
                  <Avatar name={editingUser.full_name} email={editingUser.email} />
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 14, color: G.text, margin: 0 }}>{editingUser.full_name || "Inget namn"}</p>
                    <p style={{ fontSize: 13, color: G.muted, margin: "2px 0 0", fontFamily: "'DM Mono', monospace" }}>{editingUser.email}</p>
                  </div>
                </div>

                <FL>Namn</FL>
                <input style={css.input} value={editUserFields.full_name} onChange={e => setEditUserFields(f => ({ ...f, full_name: e.target.value }))} placeholder="Förnamn Efternamn" />
                <FL>E-postadress</FL>
                <input style={css.input} value={editUserFields.email} onChange={e => setEditUserFields(f => ({ ...f, email: e.target.value }))} type="email" />
                <button onClick={updateUserProfile} disabled={editUserSaving} style={{ marginTop: 12, background: G.gold, border: "none", borderRadius: 6, color: G.bg, fontSize: 14, fontWeight: 600, padding: "9px 18px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                  {editUserSaving ? "Sparar…" : "Spara ändringar"}
                </button>

                <div style={{ marginTop: 24, paddingTop: 16, borderTop: `1px solid ${G.border}` }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: G.muted, textTransform: "uppercase", letterSpacing: "0.07em", margin: "0 0 10px" }}>Roller</p>
                  {editingUser.roles.length === 0 && <p style={{ fontSize: 14, color: G.muted, fontStyle: "italic", marginBottom: 10 }}>Inga roller tilldelade.</p>}
                  <div style={{ display: "grid", gap: 6, marginBottom: 12 }}>
                    {editingUser.roles.map(r => {
                      const ctx = [orgs.find(o => o.id === r.org_id)?.name, programs.find(p => p.id === r.program_id)?.name, courses.find(c => c.id === r.course_id)?.name].filter(Boolean).join(" › ");
                      return (
                        <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: G.surface2, borderRadius: 6, border: `1px solid ${G.border}` }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: G.text }}>{roleLabels[r.role] || r.role}</span>
                            {ctx && <span style={{ fontSize: 12, color: G.muted, marginLeft: 8 }}>{ctx}</span>}
                          </div>
                          <IconBtn danger onClick={() => deleteRoleFromUser(r.id)} title="Ta bort"><TrashIcon /></IconBtn>
                        </div>
                      );
                    })}
                  </div>

                  {/* Add role */}
                  {showForm === "editUser" && !addRoleData.showForm ? (
                    <button onClick={() => setAddRoleData({ showForm: true })} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 6, color: G.muted, fontSize: 13, padding: "6px 14px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                      + Lägg till roll
                    </button>
                  ) : addRoleData.showForm ? (
                    <div style={{ background: G.surface2, borderRadius: 8, padding: "14px", border: `1px solid ${G.border}`, marginTop: 8 }}>
                      <FL>Roll</FL>
                      <select style={{ ...css.input, marginBottom: 0 }} value={addRoleData.role || ""} onChange={e => setAddRoleData(d => ({ ...d, role: e.target.value, program_id: "", course_id: "" }))}>
                        <option value="">Välj roll…</option>
                        {Object.entries(assignableRoles).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                      <RoleScope d={addRoleData} set={d => setAddRoleData(d)} />
                      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                        <button onClick={assignRoleToUser} disabled={saving || !addRoleData.role} style={{ background: G.gold, border: "none", borderRadius: 6, color: G.bg, fontSize: 13, fontWeight: 600, padding: "7px 14px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>Tilldela</button>
                        <button onClick={() => setAddRoleData({})} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 6, color: G.muted, fontSize: 13, padding: "7px 14px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>Avbryt</button>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div style={{ marginTop: 24, paddingTop: 16, borderTop: `1px solid ${G.border}` }}>
                  {!deleteConfirm ? (
                    <button onClick={() => setDeleteConfirm(true)} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 6, color: G.red, fontSize: 13, padding: "6px 14px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", opacity: 0.85 }}>
                      Ta bort användare
                    </button>
                  ) : (
                    <div style={{ background: "#1a0e0e", border: `1px solid ${G.red}40`, borderRadius: 8, padding: "14px" }}>
                      <p style={{ fontSize: 13, color: G.red, margin: "0 0 10px" }}>Är du säker? Kontot tas bort permanent.</p>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={deleteUserAccount} disabled={editUserSaving} style={{ background: G.red, border: "none", borderRadius: 6, color: "#fff", fontSize: 13, fontWeight: 600, padding: "7px 14px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>Ja, ta bort</button>
                        <button onClick={() => setDeleteConfirm(false)} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 6, color: G.muted, fontSize: 13, padding: "7px 14px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>Avbryt</button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ── Invite User ── */}
            {showForm === "bulk" && (
              <BulkAddUsers
                G={G} css={css}
                assignableRoles={assignableRoles}
                renderScope={(d, set) => <RoleScope d={d} set={set} />}
                scopeFor={scopeFor} scopeComplete={scopeComplete}
                onClose={() => setShowForm(null)}
                onDone={() => setTimeout(() => loadAll(), 500)}
              />
            )}

            {showForm === "invite" && (
              <>
                <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 16, color: G.text, margin: "0 0 4px" }}>Lägg till användare</h3>
                <p style={{ fontSize: 13, color: G.muted, margin: "0 0 20px" }}>En inbjudningslänk skickas automatiskt via e-post.</p>
                <FL>Fullständigt namn</FL>
                <input style={css.input} type="text" placeholder="Förnamn Efternamn" value={formData.invite_full_name || ""} onChange={e => setFormData({ ...formData, invite_full_name: e.target.value })} autoFocus />
                <FL>E-postadress</FL>
                <input style={css.input} type="email" placeholder="namn@organisation.se" value={formData.invite_email || ""} onChange={e => setFormData({ ...formData, invite_email: e.target.value })} />
                <FL>Roll</FL>
                <select style={css.input} value={formData.role || ""} onChange={e => setFormData({ ...formData, role: e.target.value, program_id: "", course_id: "" })}>
                  <option value="">Välj roll…</option>
                  {Object.entries(assignableRoles).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <RoleScope d={formData} set={setFormData} />
                {inviteResult && (
                  <div style={{ marginTop: 12, padding: "10px 14px", background: inviteResult.ok ? "#0f1a0f" : "#1a0e0e", border: `1px solid ${inviteResult.ok ? G.correct : G.red}40`, borderRadius: 8, fontSize: 13, color: inviteResult.ok ? G.correct : G.red }}>
                    {inviteResult.text}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                  <button onClick={inviteUser} disabled={inviting || !formData.invite_email || !formData.role} style={{ background: G.gold, border: "none", borderRadius: 6, color: G.bg, fontSize: 14, fontWeight: 600, padding: "9px 18px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", opacity: inviting ? 0.6 : 1 }}>
                    {inviting ? "Skickar…" : "Lägg till & skicka inbjudan"}
                  </button>
                  <button onClick={() => { setShowForm(null); setFormData({}); setInviteResult(null); }} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 6, color: G.muted, fontSize: 14, padding: "9px 16px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>Avbryt</button>
                </div>
              </>
            )}

            {/* ── Org/Program/Course/Instance forms ── */}
            {["org", "program", "course", "instance"].includes(showForm) && (
              <>
                <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 16, color: G.text, margin: "0 0 20px" }}>
                  {showForm === "org" ? "Ny organisation" : showForm === "program" ? "Nytt program" : showForm === "course" ? "Ny kurs" : "Nytt kurstillfälle"}
                </h3>
                {showForm === "program" && (<><FL>Organisation</FL><select style={css.input} value={formData.org_id || ""} onChange={e => setFormData({ ...formData, org_id: e.target.value })}><option value="">Välj…</option>{orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}</select></>)}
                {showForm === "course" && (<><FL>Program</FL><select style={css.input} value={formData.program_id || ""} onChange={e => setFormData({ ...formData, program_id: e.target.value })}><option value="">Välj…</option>{programs.map(p => <option key={p.id} value={p.id}>{orgs.find(o => o.id === p.org_id)?.name} › {p.name}</option>)}</select></>)}
                {showForm === "instance" && (<><FL>Kurs</FL><select style={css.input} value={formData.course_id || ""} onChange={e => setFormData({ ...formData, course_id: e.target.value })}><option value="">Välj…</option>{courses.map(c => { const prog = programs.find(p => p.id === c.program_id); return <option key={c.id} value={c.id}>{prog?.name} › {c.name}</option>; })}</select></>)}
                <FL>{showForm === "instance" ? "Beteckning (t.ex. HT24)" : "Namn"}</FL>
                <input style={css.input} placeholder={showForm === "org" ? "t.ex. Lunds universitet" : showForm === "program" ? "t.ex. Läkarprogrammet" : showForm === "course" ? "t.ex. Anatomi 1" : "HT24"} value={formData.name || ""} onChange={e => setFormData({ ...formData, name: e.target.value })} autoFocus onKeyDown={e => e.key === "Enter" && (showForm === "org" ? createOrg() : showForm === "program" ? createProgram() : showForm === "course" ? createCourse() : createInstance())} />
                {showForm === "instance" && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div><FL>Termin</FL><select style={css.input} value={formData.term || ""} onChange={e => setFormData({ ...formData, term: e.target.value })}><option value="">—</option><option value="HT">HT</option><option value="VT">VT</option><option value="HT/VT">HT/VT</option></select></div>
                    <div><FL>År</FL><input style={css.input} type="number" placeholder="2025" value={formData.year || ""} onChange={e => setFormData({ ...formData, year: e.target.value })} /></div>
                  </div>
                )}
                <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                  <button onClick={showForm === "org" ? createOrg : showForm === "program" ? createProgram : showForm === "course" ? createCourse : createInstance} disabled={saving} style={{ background: G.gold, border: "none", borderRadius: 6, color: G.bg, fontSize: 14, fontWeight: 600, padding: "9px 18px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                    {saving ? "Sparar…" : "Skapa"}
                  </button>
                  <button onClick={() => { setShowForm(null); setFormData({}); }} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 6, color: G.muted, fontSize: 14, padding: "9px 16px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>Avbryt</button>
                </div>
              </>
            )}

            {/* ── Assign Role ── */}
            {showForm === "role" && (
              <>
                <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 16, color: G.text, margin: "0 0 4px" }}>Tilldela roll</h3>
                <p style={{ fontSize: 13, color: G.muted, margin: "0 0 20px" }}>Sök upp en befintlig användare och tilldela en roll.</p>
                <FL>Hitta användare (e-post)</FL>
                <div style={{ display: "flex", gap: 8 }}>
                  <input style={{ ...css.input, flex: 1, marginBottom: 0 }} placeholder="user@example.com" value={searchEmail} onChange={e => setSearchEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && (async () => { setSearchLoading(true); setSearchResult(null); const { data, error } = await supabase.rpc("get_user_by_email", { email_input: searchEmail.trim().toLowerCase() }); if (error || !data || data.length === 0) { setSearchResult({ notFound: true }); } else { setSearchResult(data[0]); setFormData(fd => ({ ...fd, user_id: data[0].id })); } setSearchLoading(false); })()} />
                  <button onClick={async () => { setSearchLoading(true); setSearchResult(null); const { data, error } = await supabase.rpc("get_user_by_email", { email_input: searchEmail.trim().toLowerCase() }); if (error || !data || data.length === 0) { setSearchResult({ notFound: true }); } else { setSearchResult(data[0]); setFormData(fd => ({ ...fd, user_id: data[0].id })); } setSearchLoading(false); }} disabled={searchLoading} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 6, color: G.muted, fontSize: 14, padding: "0 14px", cursor: "pointer", whiteSpace: "nowrap", fontFamily: "'DM Sans', sans-serif" }}>Sök</button>
                </div>
                {searchLoading && <p style={{ fontSize: 13, color: G.muted, marginTop: 8 }}>Söker…</p>}
                {searchResult && (
                  <div style={{ marginTop: 10, padding: "10px 14px", background: G.surface2, borderRadius: 8, border: `1px solid ${G.border}` }}>
                    {searchResult.notFound ? (
                      <><p style={{ fontSize: 13, color: G.red, margin: "0 0 8px" }}>Hittades inte. Ange User ID manuellt:</p>
                      <input style={css.input} placeholder="UUID från Supabase Auth" value={formData.user_id || ""} onChange={e => setFormData({ ...formData, user_id: e.target.value })} /></>
                    ) : (
                      <><p style={{ fontSize: 13, fontWeight: 600, color: G.correct, margin: 0 }}>✓ {searchResult.email}</p>
                      <p style={{ fontSize: 12, color: G.muted, margin: "3px 0 0", fontFamily: "'DM Mono', monospace" }}>{searchResult.id}</p></>
                    )}
                  </div>
                )}
                {formData.user_id && (<>
                  <FL>Roll</FL>
                  <select style={css.input} value={formData.role || ""} onChange={e => setFormData({ ...formData, role: e.target.value, program_id: "", course_id: "" })}>
                    <option value="">Välj roll…</option>
                    {Object.entries(assignableRoles).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <RoleScope d={formData} set={setFormData} />
                  <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                    <button onClick={assignRole} disabled={saving || !formData.role} style={{ background: G.gold, border: "none", borderRadius: 6, color: G.bg, fontSize: 14, fontWeight: 600, padding: "9px 18px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>{saving ? "Sparar…" : "Tilldela roll"}</button>
                    <button onClick={() => { setShowForm(null); setFormData({}); setSearchResult(null); setSearchEmail(""); }} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 6, color: G.muted, fontSize: 14, padding: "9px 16px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>Avbryt</button>
                  </div>
                </>)}
                {!formData.user_id && !searchResult && (
                  <button onClick={() => { setShowForm(null); setFormData({}); }} style={{ marginTop: 16, background: "none", border: `1px solid ${G.border}`, borderRadius: 6, color: G.muted, fontSize: 14, padding: "9px 16px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>Avbryt</button>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {confirmState && (
        <ConfirmDialog
          text={confirmState.text}
          lang={lang}
          G={G}
          css={css}
          onCancel={() => setConfirmState(null)}
          onConfirm={() => { const fn = confirmState.onConfirm; setConfirmState(null); if (fn) fn(); }}
        />
      )}
    </div>
  );
}

export { SuperAdminScreen };
